import React, { useState } from "react";
import Image, { ImageProps } from "next/image";

export interface OptimizedImageProps extends Omit<ImageProps, "src" | "alt" | "width" | "height"> {
  src: string;
  alt: string;
  /** Used when the original src itself fails to load. Defaults to a transparent 1x1 png. */
  fallbackSrc?: string;
  /** If true, image loads eagerly with high priority (use only for above-the-fold). */
  priority?: boolean;
  className?: string;
  width?: number | string;
  height?: number | string;
}

// A 1x1 transparent PNG so a broken asset never leaves a torn layout behind.
const TRANSPARENT_PIXEL =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=";

export const OptimizedImage = React.forwardRef<
  HTMLImageElement,
  OptimizedImageProps
>(function OptimizedImage(
  {
    src,
    alt,
    fallbackSrc,
    priority = false,
    className,
    onError,
    width,
    height,
    sizes,
    style,
    ...rest
  },
  ref,
) {
  const [hasErrored, setHasErrored] = useState(false);

  // If local path doesn't start with /, we normalize it. 
  // However, next/image already expects / for absolute path, or full URLs.
  // We mimic the previous normalizeLocalSrc behavior:
  const isPassthroughSource = (s: string) => {
    if (!s) return true;
    if (s.startsWith("data:")) return true;
    if (/^https?:\/\//i.test(s)) return true;
    return false;
  };
  const normalizeLocalSrc = (s: string) => {
    if (!s) return s;
    if (isPassthroughSource(s)) return s;
    return s.startsWith("/") ? s : `/${s}`;
  };

  const normalizedSrc = normalizeLocalSrc(src);
  const effectiveSrc = hasErrored
    ? fallbackSrc || TRANSPARENT_PIXEL
    : normalizedSrc;

  const handleError = (e: any) => {
    if (!hasErrored) setHasErrored(true);
    onError?.(e);
  };

  const hasDimensions = width !== undefined && height !== undefined;

  return (
    <Image
      ref={ref}
      src={effectiveSrc}
      alt={alt ?? ""}
      priority={priority}
      className={className}
      onError={handleError}
      width={hasDimensions ? Number(width) : 0}
      height={hasDimensions ? Number(height) : 0}
      sizes={sizes || (hasDimensions ? undefined : "100vw")}
      style={{
        width: hasDimensions ? undefined : "100%",
        height: hasDimensions ? undefined : "auto",
        ...style,
      }}
      unoptimized={hasErrored || effectiveSrc === TRANSPARENT_PIXEL}
      {...rest}
    />
  );
});

OptimizedImage.displayName = "OptimizedImage";

export default OptimizedImage;
